import { useMemo, useState } from "react";
import { ArrowDownToLine, CalendarRange, Check, FileSpreadsheet, FileText, Info, Loader2, Printer, TriangleAlert } from "lucide-react";
import type { StatementLine } from "@evocha/shared-types";
import { errorMessage, relativeTime, shortDateTime, useStatement } from "../lib/queries";
import { useSession } from "../lib/session";
import { tzs } from "../lib/format";

const TYPE_LABEL: Record<StatementLine["type"], string> = {
  funding: "Wallet top-up",
  reserve: "Airtime sale held",
  capture: "Airtime sale settled",
  release: "Reservation returned",
  commission: "Commission credit",
  refund: "Refund",
  reversal: "Reversal",
  adjustment: "Adjustment",
};

interface MonthOption {
  id: string;
  label: string;
  from: string;
  to: string;
  open: boolean;
}

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function monthOptions(count = 6): MonthOption[] {
  const now = new Date();
  const options: MonthOption[] = [];
  for (let i = 0; i < count; i += 1) {
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i + 1, 0));
    const isCurrent = i === 0;
    options.push({
      id: isoDay(start).slice(0, 7),
      label: start.toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" }),
      from: isoDay(start),
      to: isCurrent ? isoDay(now) : isoDay(end),
      open: isCurrent,
    });
  }
  return options;
}

const MONTHS = monthOptions();

export function Statements() {
  const { agent } = useSession();
  const [selectedId, setSelectedId] = useState(MONTHS[1]?.id ?? MONTHS[0]!.id);
  const [toast, setToast] = useState<string | null>(null);
  const month = MONTHS.find((m) => m.id === selectedId) ?? MONTHS[0]!;

  // The month list previews balances by fetching the 1-line statement per month;
  // for the selected month we page through the full statement.
  const statement = useStatement({ from: month.from, to: month.to, limit: 100 }, true);
  const lines = statement.data?.lines ?? [];

  const totals = useMemo(() => {
    const credit = lines.reduce((sum, l) => sum + Number(l.credit_minor), 0);
    const debit = lines.reduce((sum, l) => sum + Number(l.debit_minor), 0);
    return { credit, debit, entries: lines.length };
  }, [lines]);

  const opening = Number(statement.data?.opening_balance_minor ?? 0);
  const closing = Number(statement.data?.closing_balance_minor ?? 0);

  function notify(message: string) {
    setToast(message);
    window.setTimeout(() => setToast(null), 3600);
  }

  function exportCsv() {
    if (!lines.length) {
      notify("There are no entries in this month yet.");
      return;
    }
    const header = ["Date", "Journal", "Type", "Description", "Order", "Debit (TZS)", "Credit (TZS)", "Balance (TZS)"];
    const rows = lines.map((l) => [
      l.occurred_at,
      l.journal_ref,
      TYPE_LABEL[l.type] ?? l.type,
      `"${(l.description || "").replace(/"/g, '""')}"`,
      l.order_ref ?? "",
      (Number(l.debit_minor) / 100).toFixed(2),
      (Number(l.credit_minor) / 100).toFixed(2),
      (Number(l.balance_minor) / 100).toFixed(2),
    ]);
    const csv = [header.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `evocha-statement-${month.id}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    notify(`Exported ${lines.length} entries for ${month.label}.`);
  }

  return (
    <div className="stack-6">
      <div className="page-head">
        <div>
          <h2 className="ev-h2">Statements</h2>
          <p className="ev-muted">Monthly reconciliation for your records, your bank and TRA.</p>
        </div>
        <div className="page-head-actions">
          <button className="ev-btn ev-btn-sm ev-btn-secondary" onClick={exportCsv} disabled={!lines.length}>
            <FileSpreadsheet size={15} /> Export CSV
          </button>
          <button className="ev-btn ev-btn-sm ev-btn-primary" onClick={() => window.print()}>
            <ArrowDownToLine size={15} /> Print / PDF
          </button>
        </div>
      </div>

      <div className="statement-layout">
        <section className="ev-card statement-list">
          <div className="ev-card-head">
            <div>
              <h3 className="ev-card-title">Statements</h3>
              <p className="ev-caption">Closing balance is taken at 23:59 EAT.</p>
            </div>
            <span className="ev-badge ev-badge-neutral"><CalendarRange size={12} /> {new Date().getFullYear()}</span>
          </div>
          <div className="statement-months">
            {MONTHS.map((m) => (
              <button
                key={m.id}
                className={selectedId === m.id ? "statement-month statement-month-on" : "statement-month"}
                onClick={() => setSelectedId(m.id)}
              >
                <span className="statement-month-main">
                  <strong>{m.label}</strong>
                  <span>{m.from} → {m.to}</span>
                </span>
                <span className="statement-month-side">
                  <em className={m.open ? "statement-open" : "statement-ready"}>
                    {m.open ? "In progress" : "Ready"}
                  </em>
                </span>
              </button>
            ))}
          </div>
        </section>

        <section className="ev-card statement-sheet">
          <header className="sheet-head">
            <div>
              <span className="ev-eyebrow">evocha agent statement</span>
              <h3 className="ev-h2" style={{ marginTop: 6 }}>{month.label}</h3>
              <p className="ev-muted">{month.from} → {month.to} · {agent?.code ?? "—"} · {agent?.business_name ?? "—"}</p>
            </div>
            <div className="sheet-brand">
              <span className="ev-badge ev-badge-info">{month.id}</span>
              <span className="ev-caption">Generated {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
            </div>
          </header>

          <div className="sheet-summary">
            <div><span>Closing balance</span><strong>{statement.isLoading ? "…" : tzs(closing)}</strong></div>
            <div><span>Opening balance</span><strong>{statement.isLoading ? "…" : tzs(opening)}</strong></div>
            <div><span>Money in</span><strong className="ev-pos">{statement.isLoading ? "…" : tzs(totals.credit)}</strong></div>
            <div><span>Money out</span><strong>{statement.isLoading ? "…" : tzs(totals.debit)}</strong></div>
          </div>

          {month.open && (
            <div className="sheet-note">
              <Info size={15} />
              <span>This month is still open. Figures include activity up to {month.to} and will change until month end.</span>
            </div>
          )}

          {statement.isError ? (
            <div className="sheet-note">
              <TriangleAlert size={15} />
              <span>{errorMessage(statement.error)}</span>
            </div>
          ) : (
            <table className="ev-table sheet-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Line item</th>
                  <th>Description</th>
                  <th className="ev-num">Debit</th>
                  <th className="ev-num">Credit</th>
                  <th className="ev-num">Balance</th>
                </tr>
              </thead>
              <tbody>
                {statement.isLoading && (
                  <tr><td colSpan={6} className="ev-muted" style={{ textAlign: "center", padding: 24 }}><Loader2 size={18} className="spin" /></td></tr>
                )}
                {!statement.isLoading && lines.length === 0 && (
                  <tr><td colSpan={6} className="ev-muted" style={{ textAlign: "center", padding: 24 }}>No ledger entries in this period.</td></tr>
                )}
                {lines.map((l) => (
                  <tr key={`${l.journal_ref}-${l.occurred_at}`}>
                    <td className="ev-muted">{new Date(l.occurred_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</td>
                    <td><strong>{TYPE_LABEL[l.type] ?? l.type}</strong></td>
                    <td className="ev-muted">
                      {l.description || "—"}
                      {l.order_ref && <span className="ev-mono" style={{ marginLeft: 6 }}>{l.order_ref}</span>}
                    </td>
                    <td className={`ev-num ${Number(l.debit_minor) ? "" : "sheet-zero"}`}>{Number(l.debit_minor) ? `−${tzs(l.debit_minor)}` : "—"}</td>
                    <td className={`ev-num ev-pos ${Number(l.credit_minor) ? "" : "sheet-zero"}`}>{Number(l.credit_minor) ? `+${tzs(l.credit_minor)}` : "—"}</td>
                    <td className="ev-num ev-muted">{tzs(l.balance_minor)}</td>
                  </tr>
                ))}
                <tr className="sheet-total">
                  <td colSpan={3}><strong>Net movement</strong></td>
                  <td className="ev-num">−{tzs(totals.debit)}</td>
                  <td className="ev-num ev-pos">+{tzs(totals.credit)}</td>
                  <td className={`ev-num ${closing >= opening ? "ev-pos" : "ev-neg"}`}>{tzs(closing)}</td>
                </tr>
              </tbody>
            </table>
          )}

          <div className="sheet-foot">
            <div className="sheet-meta">
              <span>Entries <strong>{totals.entries}</strong></span>
              <span>Last entry <strong>{lines[0] ? relativeTime(lines[0].occurred_at) : "—"}</strong></span>
              {lines[0] && <span>Booked <strong>{shortDateTime(lines[0].occurred_at)}</strong></span>}
            </div>
            <div className="sheet-actions">
              <button className="ev-btn ev-btn-sm ev-btn-secondary" onClick={exportCsv} disabled={!lines.length}><FileSpreadsheet size={14} /> CSV</button>
              <button className="ev-btn ev-btn-sm ev-btn-secondary" onClick={() => window.print()}><FileText size={14} /> PDF</button>
              <button className="ev-btn ev-btn-sm ev-btn-ghost" onClick={() => window.print()}><Printer size={14} /> Print</button>
            </div>
          </div>

          <p className="sheet-legal">
            evocha is a product of SKT Tanzania Limited. This statement is generated from the evocha ledger and is provided
            for reconciliation. Queries must be raised within 30 days.
          </p>
        </section>
      </div>

      {toast && <div className="toast" role="status"><Check size={16} /> {toast}</div>}
    </div>
  );
}
